/**
 * Banner Alert Action - WHAT Section
 *
 * Edit mode UI for assigning an app banner alert.
 * Used inline within AutomationCard when editing the "What" section.
 *
 * V2 Reference: v2/app/templates/components/client-reminder-banner-alert.hbs
 *
 * Fields:
 * - reminderOnId: Selected banner alert ID
 * - reminderOnType: 'ModalAlert' (banner alerts use same model)
 * - expireAfterDays: Days until banner unassigns
 * - onlyShowForOs: OS filter (all, ios, android)
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { fetchBannerAlerts } from 'src/models/conversation/conversation';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';

import { ActionWhatSectionProps } from '../automationActions';

/**
 * BannerAlert WHAT Section - Edit mode
 */
export const BannerAlertWhatSection: FC<ActionWhatSectionProps> = ({
  automation,
  index,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  // V2 uses null for "All" to match backend expectations
  const osOptions = [
    { value: null as unknown as string, label: t('automations.bannerAlert.all') },
    { value: 'ios', label: t('automations.bannerAlert.ios') },
    { value: 'android', label: t('automations.bannerAlert.android') },
  ];

  // Fetch available banner alerts
  const { data, isLoading } = useQuery({
    queryKey: ['banner-alerts'],
    queryFn: fetchBannerAlerts,
    staleTime: 60000,
  });

  const alerts = data?.alerts || [];
  const alertOptions = alerts.map((a) => ({
    value: a.id.toString(),
    label: a.title,
  }));

  const handleAlertChange = (alertId: string) => {
    setFieldValue(`automations.${index}.reminderOnId`, alertId);
    setFieldValue(`automations.${index}.reminderOnType`, 'ModalAlert');
  };

  return (
    <Stack spacing={2}>
      {/* Banner Alert selector */}
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Box sx={{ minWidth: 200 }}>
          <FormSelect
            name={`automations.${index}.reminderOnId`}
            options={alertOptions}
            prompt={isLoading ? t('automations.common.loading') : t('automations.bannerAlert.selectAppBannerAlert')}
            onChange={(e) => handleAlertChange(e.target.value as string)}
          />
        </Box>
      </Box>

      {/* Duration */}
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Box>{t('automations.bannerAlert.unassignAfter')}</Box>
        <TextField
          type="number"
          value={automation.expireAfterDays || ''}
          onChange={(e) =>
            setFieldValue(
              `automations.${index}.expireAfterDays`,
              e.target.value ? parseInt(e.target.value) : null
            )
          }
          placeholder={t('automations.bannerAlert.never')}
          sx={{ width: 80 }}
          size="small"
          InputProps={{ inputProps: { min: 1 } }}
        />
        <Box>{t('automations.bannerAlert.days')}</Box>
      </Box>

      {/* OS Filter */}
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Box>{t('automations.bannerAlert.showOn')}</Box>
        <Box sx={{ minWidth: 120 }}>
          <FormSelect
            name={`automations.${index}.onlyShowForOs`}
            options={osOptions}
          />
        </Box>
        <Box>{t('automations.bannerAlert.apps')}</Box>
      </Box>
    </Stack>
  );
};

/**
 * BannerAlert What Summary Component
 */
export const BannerAlertWhatSummary: FC<{
  automation: ActionWhatSectionProps['automation'];
}> = ({ automation }) => {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['banner-alerts'],
    queryFn: fetchBannerAlerts,
    staleTime: 60000,
  });

  const alerts = data?.alerts || [];

  if (!automation.reminderOnId) {
    return <i>{t('automations.bannerAlert.noAppBannerSelected')}</i>;
  }

  const alert = alerts.find(
    (a) => a.id.toString() === automation.reminderOnId?.toString()
  );
  const alertTitle = alert?.title || `Banner #${automation.reminderOnId}`;

  const duration = automation.expireAfterDays
    ? `${t('automations.bannerAlert.unassignAfter')} ${automation.expireAfterDays} ${t('automations.bannerAlert.days')}`
    : t('automations.bannerAlert.untilNextBannerAssigned');

  // OS setting display
  const osValue = automation.onlyShowForOs;
  const osLabel = osValue === 'ios'
    ? t('automations.bannerAlert.iosOnly')
    : osValue === 'android'
    ? t('automations.bannerAlert.androidOnly')
    : t('automations.bannerAlert.allPlatforms');

  return (
    <>
      {alertTitle} - {duration} ({osLabel})
    </>
  );
};
